export interface CatalogVideo {
  id: { videoId: string };
  snippet: {
    title: string;
    description?: string;
    channelTitle?: string;
    channelId?: string;
    publishedAt?: string;
    thumbnails?: Partial<Record<"default" | "medium" | "high" | "standard" | "maxres", { url: string; width?: number; height?: number }>>;
  };
  contentDetails?: { duration?: number };
}

export type VideoKind = "music-video" | "visualizer" | "lyric" | "live" | "remix" | "audio" | "feature" | "other";

export const VIDEO_KIND_LABELS: Record<VideoKind, { one: string; many: string }> = {
  "music-video": { one: "Music video", many: "Music videos" },
  visualizer: { one: "Visualizer", many: "Visualizers" },
  lyric: { one: "Lyric video", many: "Lyric videos" },
  live: { one: "Live", many: "Live and sessions" },
  remix: { one: "Remix", many: "Remixes" },
  audio: { one: "Audio", many: "Official audio" },
  feature: { one: "Feature", many: "Features" },
  other: { one: "Video", many: "More from the channel" },
};

export const VIDEO_SECTION_ORDER: VideoKind[] = ["music-video", "visualizer", "lyric", "live", "remix", "audio", "feature", "other"];

export const WATCHABLE_KINDS: VideoKind[] = ["music-video", "visualizer", "lyric", "live"];

export const MIN_SECTION_SIZE = 2;

export interface CatalogEntry {
  video: CatalogVideo;
  videoId: string;
  title: string;
  kind: VideoKind;
  publishedAt: number | null;
  durationSeconds: number;
  ownChannel: boolean;
}

export interface VideoSection {
  key: string;
  title: string;
  kind: VideoKind | null;
  entries: CatalogEntry[];
}

const ENTITY_MAP: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", "#39": "'", "#039": "'", nbsp: " " };

export function decodeEntities(value: string | undefined | null): string {
  if (!value) return "";
  return value
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
      const lower = code.toLowerCase();
      if (ENTITY_MAP[lower] !== undefined) return ENTITY_MAP[lower];
      if (lower.startsWith("#x")) return String.fromCodePoint(parseInt(lower.slice(2), 16));
      if (lower.startsWith("#")) return String.fromCodePoint(parseInt(lower.slice(1), 10));
      return match;
    })
    .trim();
}

const normalize = (value: string | undefined | null) =>
  decodeEntities(value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export function isOwnChannel(channelTitle: string | undefined, artistName: string | undefined): boolean {
  const artist = normalize(artistName);
  if (!artist) return true;
  return normalize(channelTitle).includes(artist);
}

export function classifyVideo(video: CatalogVideo, artistName?: string): VideoKind {
  const title = normalize(video.snippet?.title);
  const channel = decodeEntities(video.snippet?.channelTitle);
  if (!isOwnChannel(channel, artistName)) return "feature";
  if (/\blyrics?\b/.test(title)) return "lyric";
  if (/\bvisuali[sz]er\b/.test(title)) return "visualizer";
  if (/\b(live|performance|session|sessions|acoustic|concert|unplugged)\b/.test(title)) return "live";
  if (/\bofficial (music )?video\b|\bmusic video\b|\bmv\b/.test(title)) return "music-video";
  if (/\bremix(es)?\b|\brmx\b/.test(title)) return "remix";
  if (/\b(official )?audio\b/.test(title) || /\s-\s*topic$/i.test(channel)) return "audio";
  return "other";
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function displayTitle(title: string, artistName?: string): string {
  const artist = decodeEntities(artistName);
  if (!artist) return title;
  const stripped = title.replace(new RegExp(`^${escapeRegExp(artist)}\\s*[-–—+:|]\\s*`, "i"), "").trim();
  return stripped || title;
}

const parseDate = (value: string | undefined) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

export function toCatalogEntries(videos: CatalogVideo[] | null | undefined, artistName?: string): CatalogEntry[] {
  const seen = new Set<string>();
  const entries: CatalogEntry[] = [];
  for (const video of videos || []) {
    const videoId = video?.id?.videoId;
    if (!videoId || seen.has(videoId)) continue;
    seen.add(videoId);
    entries.push({
      video,
      videoId,
      title: displayTitle(decodeEntities(video.snippet?.title) || "Untitled video", artistName),
      kind: classifyVideo(video, artistName),
      publishedAt: parseDate(video.snippet?.publishedAt),
      durationSeconds: Math.max(0, Math.round(video.contentDetails?.duration || 0)),
      ownChannel: isOwnChannel(video.snippet?.channelTitle, artistName),
    });
  }
  return entries;
}

const byNewest = (a: CatalogEntry, b: CatalogEntry) => (b.publishedAt ?? 0) - (a.publishedAt ?? 0);

export function pickFeaturedVideo(entries: CatalogEntry[]): CatalogEntry | null {
  if (entries.length === 0) return null;
  const newest = [...entries].sort(byNewest);
  return (
    newest.find((entry) => entry.ownChannel && WATCHABLE_KINDS.includes(entry.kind)) ||
    newest.find((entry) => entry.ownChannel && entry.kind !== "audio") ||
    newest.find((entry) => entry.ownChannel) ||
    newest[0]
  );
}

const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export function recencySections(entries: CatalogEntry[], now: number): VideoSection[] {
  const sorted = [...entries].sort(byNewest);
  const recent = sorted.filter((entry) => entry.publishedAt !== null && now - entry.publishedAt <= YEAR_MS);
  const earlier = sorted.filter((entry) => !recent.includes(entry));
  const sections: VideoSection[] = [];
  if (recent.length > 0) sections.push({ key: "recent", title: "From the past year", kind: null, entries: recent });
  if (earlier.length > 0) sections.push({ key: "earlier", title: recent.length > 0 ? "Earlier" : "All videos", kind: null, entries: earlier });
  return sections;
}

export function buildVideoSections(entries: CatalogEntry[], now: number, minSize = MIN_SECTION_SIZE): VideoSection[] {
  const groups = new Map<VideoKind, CatalogEntry[]>();
  for (const entry of entries) {
    const list = groups.get(entry.kind) ?? [];
    list.push(entry);
    groups.set(entry.kind, list);
  }
  const sections: VideoSection[] = [];
  const leftovers: CatalogEntry[] = [];
  for (const kind of VIDEO_SECTION_ORDER) {
    const list = groups.get(kind);
    if (!list || list.length === 0) continue;
    if (kind === "other" || list.length < minSize) {
      leftovers.push(...list);
      continue;
    }
    sections.push({ key: kind, title: VIDEO_KIND_LABELS[kind].many, kind, entries: [...list].sort(byNewest) });
  }
  if (sections.length < 2) return recencySections(entries, now);
  if (leftovers.length > 0) {
    sections.push({ key: "other", title: VIDEO_KIND_LABELS.other.many, kind: "other", entries: leftovers.sort(byNewest) });
  }
  return sections;
}

export function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds < 1) return "";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const pad = (value: number) => String(value).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

export function spokenDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds < 1) return "";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const parts = [];
  if (minutes > 0) parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
  if (seconds > 0) parts.push(`${seconds} second${seconds === 1 ? "" : "s"}`);
  return parts.join(" ");
}

export function formatPublished(timestamp: number | null): string {
  if (timestamp === null) return "";
  return new Date(timestamp).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

export function thumbnailFor(video: CatalogVideo, size: "card" | "hero"): string | undefined {
  const thumbs = video.snippet?.thumbnails || {};
  if (size === "hero") return thumbs.maxres?.url || thumbs.standard?.url || thumbs.high?.url || thumbs.medium?.url || thumbs.default?.url;
  return thumbs.high?.url || thumbs.medium?.url || thumbs.default?.url;
}

export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
}

export function descriptionExcerpt(description: string | undefined, maxLength = 220): string {
  const text = decodeEntities(description).replace(/https?:\/\/\S+/g, "").replace(/\s+/g, " ").trim();
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > maxLength * 0.6 ? lastSpace : maxLength).trim()}…`;
}
